<template>
  <div
    v-if="status === 'loading' || eventsNumber > 0"
    ref="widgetContainer"
    :class="[
      'szw-bg-background szw-font-family szw-core szw-w-full szw-relative szw-flex szw-items-center szw-text-text-icon-match szw-overflow-hidden',
      { 'szw-gap-2': hasControls && !isSmallWidgetContainerSize },
      isSmallWidgetContainerSize ? ' szw-h-[70px]' : 'szw-h-[80px]'
    ]"
    :e2e-id="`szw-stripe-match-preview-widget`"
    :theme-id="activeTheme"
    :lang-id="languageIdentifier"
    :drf-id="`${widgetId}-${props.id}`"
  >
    <div
      v-if="['mozzart', 'soccer-bet'].includes(activeTheme)"
      ref="brandingContainerRef"
      :class="['szw-h-full szw-w-[78px] szw-flex szw-flex-col szw-items-center szw-justify-center szw-pl-2 szw-py-1.5 szw-gap-1.5', { 'szw-mr-2': status === 'loading' }]"
    >
      <sprite :sprite-id="`${activeTheme}-logo`" :key="activeTheme" />
      <span class="szw-text-xxs szw-text-text-icon-tertiary szw-whitespace-nowrap szw-font-semibold szw-select-none szw-leading-xxs">POWERED BY</span>
    </div>

    <stripe-horizontal-view
      v-if="status === 'success' && eventsNumber > 0"
      :list="events"
      :widget-container-width="widgetContainerWidth - brandingContainerWidth - (hasControls && !isSmallWidgetContainerSize ? 8 : 0)"
      :is-small-widget-container-size="isSmallWidgetContainerSize"
      :language-identifier="languageIdentifier"
      @has-controls="handleHasControls"
    />
    <stripe-match-skeleton v-else :is-mobile-view="isSmallWidgetContainerSize" />
  </div>
</template>

<script setup>
import '@/__core__/style/style.scss'
import 'vue3-carousel/dist/carousel.css'
import { computed, provide, ref } from 'vue'
import useFetch from '@/__core__/composables/useFetch'
import useReactors from '@/__core__/composables/useReactors'
import useSize from '@/__core__/composables/useSize'
import { useStripeStore } from './Stripe.store'
import { useDataRefresh } from '@/__core__/data-refresh/useDataRefresh'
import StripeHorizontalView from './components/StripeHorizontalView.vue'
import StripeMatchSkeleton from './components/StripeMatchSkeleton.vue'
import { CDN } from '@/__core__/cdn/cdn'
import Sprite from '@/__core__/components/sprite/Sprite.vue'

const props = defineProps({
  id: {
    type: String,
    default: 'common'
  },
  languageIdentifier: {
    type: String,
    default: 'eng'
  },
  theme: {
    type: String,
    default: 'light'
  },
  size: {
    type: Number,
    default: 640
  },
  after: {
    type: Boolean,
    default: true
  },
  before: {
    type: Boolean,
    default: true
  },
  groupByCompetition: {
    type: Boolean,
    default: true
  },
  sportTypeRequests: {
    type: Array,
    default: () => [{ sportType: 'FOOTBALL', seasonIds: ['sr:season:118689', 'sr:season:118691', 'sr:season:118975', 'sr:season:118693'] }]
  },
  sortSample: {
    type: Array,
    default: () => [
      { seasonId: 'sr:season:118689', competitionId: 'sr:competition:17', name: 'Premier League', sportType: 'FOOTBALL' },
      { seasonId: 'sr:season:118691', competitionId: 'sr:competition:8', name: 'LaLiga', sportType: 'FOOTBALL' },
      { seasonId: 'sr:season:118975', competitionId: 'sr:competition:23', name: 'Serie A', sportType: 'FOOTBALL' },
      { seasonId: 'sr:season:118693', competitionId: 'sr:competition:35', name: 'Bundesliga', sportType: 'FOOTBALL' }
    ]
  },
  date: {
    type: String,
    default: () => '2025-02-03'
  },
  themeConfig: {
    type: Object,
    default: () => null
  }
})

const widgetId = 'events-stripe'

/** @type {Ref<HTMLDivElement>} */
const widgetContainer = ref(null)
/** @type {Ref<HTMLDivElement>} */
const brandingContainerRef = ref(null)
const hasControls = ref(false)

const stripeStore = useStripeStore()

const { activeTheme, activeSize } = useReactors(props.theme, props.size, { id: 'events-stripe' }, widgetContainer, undefined, props.themeConfig, 'latest')
const { width: widgetContainerWidth } = useSize(widgetContainer, activeSize)

const isSmallWidgetContainerSize = computed(() => widgetContainerWidth.value <= 480)

const { startRefresh } = useDataRefresh(60 * 1000, `${widgetId}-${props.id}`)

// Constants
const ONE_DAY_MS = 864e5

// Computed property for datesList
const datesList = date => {
  const baseDate = new Date(date)
  const formatDate = offset => new Date(baseDate.getTime() + offset).toISOString().split('T')[0]

  return [...(props.before ? [formatDate(-ONE_DAY_MS)] : []), formatDate(0), ...(props.after ? [formatDate(ONE_DAY_MS)] : [])]
}

const sportTypeRequestsAdapted = computed(() => props.sportTypeRequests.map(item => (item.sportType === 'BASKETBALL' ? { ...item, sportType: 'GLOBAL_BASKETBALL' } : item)))

const { status } = useFetch(async () => {
  const dates =
    CDN.getParentUrl().includes('sports-dash-cms-newsportal') ||
    CDN.getParentUrl().includes('sportsmode-ui') ||
    CDN.getCdnUrl().includes('sportsmode.int-green') ||
    CDN.getCdnUrl() === '/'
      ? datesList(props.date)
      : datesList(Date.now())

  const payload = {
    stripeApiPayload: {
      sportTypeRequests: sportTypeRequestsAdapted.value,
      dates
    },
    sportType: props.sportType,
    languageIdentifier: props.languageIdentifier,
    groupByCompetition: props.groupByCompetition,
    sortSample: props.sortSample,
    widgetId
  }

  await stripeStore.fetchStripeData(payload)

  const refreshFn = async () => stripeStore.scheduleStripeRefresh(payload, !props.date)
  const shouldCallApi = () => stripeStore.getShouldCallApi()
  const shouldStopRefresh = () => stripeStore.getShouldStopRefresh
  const getIsPaused = () => stripeStore.getIsPaused
  startRefresh(refreshFn, shouldCallApi, shouldStopRefresh, getIsPaused)
})

const eventsNumber = computed(() => stripeStore.events.length)
const events = computed(() => {
  return isSmallWidgetContainerSize.value ? stripeStore.events.filter(event => event.kind === 'event') : stripeStore.events
})
const brandingContainerWidth = computed(() => brandingContainerRef.value?.offsetWidth || 0)
const handleHasControls = _hasControls => {
  hasControls.value = _hasControls
}

provide('status', status)
provide('activeSize', activeSize)
provide('langId', props.languageIdentifier)
provide('activeTheme', activeTheme)
provide('themeVersion', 'latest')
</script>
